import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '../../components/PageShell';
import Tabs from '../../components/Tabs';
import { useHubExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import PrintReportDocument from '../PrintReportDocument';
import InfoTooltip from '../../components/InfoTooltip';
import { usePersistentValue } from '../../hooks/usePersistentState';
import LabourCostRatePage from './LabourCostRatePage';
import CcrStory from './CcrStory';
import { resolveTabs } from '../../i18n/resolveTabs';
import { useP } from '../../i18n/useP';

/* Salaire horaire (LCR) — deux lectures depuis le 2026-08-22, comme la section
   Coût horaire : la MÉTHODE d'abord, le résultat ensuite.

   Le récit est le même composant que celui du CCR, coupé en deux : les trois
   premiers chapitres construisent le salaire horaire (calendrier → heures par
   jour → salaire horaire), les cinq suivants le coût horaire. Ils forment une
   seule chaîne, on la lit là où elle sert. */
const TAB_DEFS = [
  { id: 'story', labelKey: 'people:ccr.tabs.story' },
  { id: 'lcr',   labelKey: 'people:lcr.tabs.lcr' },
  /* (Onglet « Temps » retiré le 2026-08-27 : il montait ici la même vue que la
     section Temps de People. L'original y reste ; les renvois du récit y
     conduisent.) */
];


export default function LabourRateHub({ activeTenant, addToast, year, onYearChange, setPage }) {
  const { t } = useTranslation();
  const p = useP();
  const [tab, setTab] = useState('story');
  const [unit, setUnit] = usePersistentValue(`lcr_hub_unit_${activeTenant?.id || ''}`, 'chf');
  /* L'unité monte à la BANDE DE CONTEXTE, comme dans Finance (2026-09-18) :
     elle tenait la droite de la ligne des onglets, où elle changeait de place
     d'un onglet à l'autre. Le gabarit la place, la page la déclare. */
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);
  const TABS = resolveTabs(TAB_DEFS, t);

  /* Export Excel → registre, rendu par l'icône télécharger de la barre supérieure
     (CHF forcé). Il sortait UNE feuille — le salaire horaire seul. Or le hub en
     porte davantage : les CINQ étapes du plan de calcul, dont chacune réduit
     le tableau à ses propres colonnes. Une étape = une feuille (2026-08-23,
     la feuille du temps standard partie avec son onglet le 2026-08-27). Le récit reste
     dehors : c'est un texte à défilement, il n'a pas de tableau à donner. */
  const LCR_SHEETS = [
    ['rem',   p('Rémunération')],
    ['cs',    p('Charges sociales')],
    ['masse', p('Masse salariale chargée')],
    ['to',    p('Temps ouvert (TO)')],
    ['lcr',   t('people:lcr.title')],
  ];
  const [showPrint, setShowPrint] = useState(false);
  const excel = useHubExcelExport({
    tabs: [
      ...LCR_SHEETS.map(([id, label]) => ({
        label,
        render: () => <LabourCostRatePage embedded unit="chf" focus={id} activeTenant={activeTenant} year={year} />,
      })),
    ],
    fileBase: 'Salaire horaire (LCR)',
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  /* PDF de synthèse — le salaire horaire par personne. */
  useRegisterPageExports(useMemo(() => ([
    { id: 'excel', label: 'Excel', run: excel.run },
    { id: 'pdf', label: 'PDF', run: () => setShowPrint(true) },
  ]), [excel.run]));

  return (
    <PageShell
      framed
      kind="report"
      unit={tab === 'lcr' ? pageUnit : null}
      title={<>{t('people:lcr.title')}<InfoTooltip term="lcr" /></>}
    >
      <Tabs tabs={TABS} active={tab} onChange={setTab} />
      <div className="page-shell__panel">
        {tab === 'story' && (
          /* Les renvois du récit vers un onglet ABSENT d'ici conduisent à la
             section qui le porte vraiment (le Temps de People). */
          <CcrStory part="lcr" activeTenant={activeTenant} year={year} unit={unit}
                    setPage={setPage} onGoDetail={() => setTab('lcr')}
                    onGoTab={(id) => {
                      if (id === 'story' || id === 'lcr') setTab(id);
                      else if (id === 'settings') setPage && setPage('cost-rates-hub');
                      else setPage && setPage('time-standard');
                    }} />
        )}
        {tab === 'lcr' && (
          <LabourCostRatePage embedded unit={unit} activeTenant={activeTenant} year={year} />
        )}
      </div>
      {showPrint && (
        <PrintReportDocument
          activeTenant={activeTenant}
          year={year}
          reports={['labour-rate']}
          mode="pdf"
          onClose={() => setShowPrint(false)}
        />
      )}

      {excel.stage}
    </PageShell>
  );
}
